import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [status, setStatus] = useState<'checking' | 'ready' | 'invalid'>('checking');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      return;
    }
    let cancelled = false;
    fetch(`/api/v1/auth/reset-password/validate?token=${encodeURIComponent(token)}`)
      .then((res) => {
        if (cancelled) return;
        setStatus(res.ok ? 'ready' : 'invalid');
      })
      .catch(() => {
        if (!cancelled) setStatus('invalid');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (newPw !== confirmPw) {
      setError('Passwords do not match.');
      return;
    }
    if (newPw.length < 12) {
      setError('New password must be at least 12 characters.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch('/api/v1/auth/reset-password', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword: newPw }),
      });
      if (!res.ok) {
        const json = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
        setError(json?.error?.message ?? 'Could not reset password.');
        if (res.status === 410) setStatus('invalid');
        return;
      }
      await refresh();
      navigate('/dashboard', { replace: true });
    } catch {
      setError('Network error. Try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login">
      <div className="login__panel">
        <div className="login__brand">
          <img src="/design-system/assets/VIN-light.svg" alt="VIN" />
          <span className="login__brand-tag">AI Software Registry</span>
        </div>
        <div className="login__hello">Set a new password</div>
        <h1 className="login__headline">Almost there.</h1>
        <p className="login__lede">
          Pick a new password — minimum 12 characters. After you save it you'll be signed in
          automatically.
        </p>
      </div>

      <div className="login__form-side">
        <div className="login__card">
          {status === 'checking' && (
            <p className="muted">Checking your link…</p>
          )}

          {status === 'invalid' && (
            <>
              <h2 className="login__title">Link expired</h2>
              <p className="login__sub" style={{ marginBottom: 22 }}>
                This reset link is no longer valid — they expire after 1 hour or after one use. Request
                a new one to continue.
              </p>
              <Link to="/forgot-password" className="btn btn--primary btn--block btn--lg">
                Request a new link
              </Link>
            </>
          )}

          {status === 'ready' && (
            <>
              <h2 className="login__title">Set new password</h2>
              <form className="login__fields" onSubmit={(e) => void onSubmit(e)}>
                <div className="field">
                  <label className="field__label" htmlFor="newPw">New password</label>
                  <input
                    id="newPw"
                    type="password"
                    value={newPw}
                    autoComplete="new-password"
                    minLength={12}
                    onChange={(e) => setNewPw(e.target.value)}
                    required
                  />
                  <p className="field__help">Minimum 12 characters.</p>
                </div>
                <div className="field">
                  <label className="field__label" htmlFor="confirmPw">Confirm new password</label>
                  <input
                    id="confirmPw"
                    type="password"
                    value={confirmPw}
                    autoComplete="new-password"
                    onChange={(e) => setConfirmPw(e.target.value)}
                    required
                  />
                </div>
                {error && <div className="field__error">{error}</div>}
                <button
                  className="btn btn--primary btn--block btn--lg"
                  type="submit"
                  disabled={submitting}
                >
                  {submitting ? 'Saving…' : 'Save and sign in'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
